import moment from "moment-timezone";
import i18n from "@/i18n";
import store from "@/store";

const week_days = [
  {
    name: "Monday",
    short: "mon",
  },
  {
    name: "Tuesday",
    short: "tue",
  },
  {
    name: "Wednesday",
    short: "wed",
  },
  {
    name: "Thursday",
    short: "thu",
  },
  {
    name: "Friday",
    short: "fri",
  },
  {
    name: "Saturday",
    short: "sat",
  },
  {
    name: "Sunday",
    short: "sun",
  },
];

const getSchedulingProfileURL = (id) => {
  return `${process.env.VUE_APP_AWS}callendo/profile/200x200/${id}`;
};

const getSchedulingBrandURL = (id) => {
  return `${process.env.VUE_APP_AWS}callendo/brand/${id}`;
};

const getSchedulingBGURL = (id) => {
  return `${process.env.VUE_APP_AWS}callendo/background/${id}`;
};

const getSchedulingVideoURL = (id) => {
  if (!id) return null;
  if (id.startsWith("blob") | id.startsWith("http")) return id;
  else return `${process.env.VUE_APP_AWS}callendo/video/${id}`;
};

const getOverlayURL = (id) => {
  if (!id) return null;
  if (id.startsWith("blob") | id.startsWith("http")) return id;
  else {
    let userID = id.split("-")[0];
    return `${process.env.VUE_APP_CLOUDFRONT}/dynamicVideos/user_${userID}/overlays/${id}`;
  }
};

const getRecordingVideoURL = (id) => {
  try {
    let ids = id.split("_");
    return `${process.env.VUE_APP_CLOUDFRONT}/user_${ids[0]}/${id}`;
  } catch (e) {
    return "";
  }
};

const getDynamicVideoURL = (id) => {
  try {
    let ids = id.split("_");
    return `${process.env.VUE_APP_CLOUDFRONT}/dynamicVideos/user_${ids[0]}/${id}`;
  } catch (e) {
    return "";
  }
};

const zones = moment.tz.names();

const option_locations = [
  {
    location_type: "address",
    name: "In-person meeting",
    class: "la la-map-marker color-darkorange",
    description: "Set an address or place",
    is_require_value: true,
  },
  {
    location_type: "phone_call",
    name: "Phone call",
    class: "la la-phone-volume color-primary",
    description: "Inbound or outbound calls",
    is_require_value: true,
  },
  {
    location_type: "google_meet",
    name: "Google Meet",
    class: "google_meet",
    description: "Web conference",
    is_require_value: false,
  },
  {
    location_type: "microsoft_teams",
    name: "Microsoft Teams",
    class: "microsoft_teams",
    description: "Web conference",
    is_require_value: false,
  },
  {
    location_type: "zoom",
    name: "Zoom",
    class: "zoom",
    description: "Web conference",
    is_require_value: false,
  },
  {
    location_type: "custom",
    name: "Custom",
    class: "la la-coffee color-darkviolet",
    description: "Leave customized location details",
    is_require_value: true,
  },
];

const getLocationIcon = (type, size = 24) => {
  if (type == "google_meet") return '<img src="' + require("@/assets/img/icons/google-meet.png") + '" alt="" width="' + size + 'px" />';
  else if (type == "microsoft_teams") return '<img src="' + require("@/assets/img/icons/teams.png") + '" alt="" width="' + size + 'px" />';
  else if (type == "zoom") return '<img src="' + require("@/assets/img/icons/zoom.png") + '" alt="" width="' + size + 'px" />';
  else if (type == "address") return '<i class="la la-map-marker color-darkorange' + (size > 15 ? " font-h4" : "") + '"></i>';
  else if (type == "phone_call") return '<i class="la la-phone-volume color-primary' + (size > 15 ? " font-h4" : "") + '"></i>';
  else if (type == "custom") return '<i class="la la-coffee color-darkviolet' + (size > 15 ? " font-h4" : "") + '"></i>';
  else return "";
};

const getLocationName = (type, value) => {
  if (type == "google_meet") return "Google Meet";
  else if (type == "microsoft_teams") return "Microsoft Teams";
  else if (type == "zoom") return "Zoom";
  else return value;
};

const list_questions = [
  {
    question_type: "text",
    class: "la la-pen-alt",
    name: "Text",
    description: "Up to 255 characters",
  },
  {
    question_type: "large_text",
    class: "la la-align-left",
    name: "Large Text",
    description: "For text longer than 255 characters",
  },
  {
    question_type: "single_options",
    class: "la la-check-circle",
    name: "Single options",
    description: "List of predefined options where only one can be selected",
  },
  {
    question_type: "multiple_options",
    class: "la la-check-square",
    name: "Multiple options",
    description: "List of predefined options where miltiple can be selected",
  },
  {
    question_type: "dropdown",
    class: "la la-tasks",
    name: "Dropdown",
    description: "List of predefined options where only one can be selected",
  },
  {
    question_type: "phone_number",
    class: "la la-phone",
    name: "Phone number",
    description: "Let users fill in their phone number",
  },
];

const newSalt = (length = 8) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

const integration_types = [
  {
    icon: "la la-video",
    name: "Video conferencing",
    items: [
      {
        id: "zoom",
        name: "Zoom",
        description: "text.int_zoom_msg",
        image: require("@/assets/img/icons/zoom.png"),
      },
      {
        id: "google_meet",
        name: "Google Meet",
        description: "text.int_google_meet_msg",
        image: require("@/assets/img/icons/google-meet.png"),
      },
      {
        id: "microsoft_teams",
        name: "Microsoft Teams",
        description: "text.int_teams_msg",
        image: require("@/assets/img/icons/teams.png"),
      },
    ],
  },
  {
    icon: "la la-calendar",
    name: "Calendars",
    items: [
      {
        id: "google_calendar",
        name: "Google Calendar",
        description: "text.int_google_calendar_msg",
        image: require("@/assets/img/icons/google-calendar.png"),
      },
      {
        id: "outlook_calendar",
        name: "Outlook Calendar",
        description: "text.int_outlook_meet_msg",
        image: require("@/assets/img/icons/outlook-calendar.png"),
      },
      {
        id: "exchange_calendar",
        name: "Exchange",
        description: "text.int_exchange_msg",
        image: require("@/assets/img/icons/exchange.png"),
      },
    ],
  },
  {
    icon: "la la-plug",
    name: "Native integrations",
    items: [
      {
        id: "heyreach",
        name: "Heyreach",
        description: "Create & Send AI videos from Heyreach, and book meetings with your leads all directly from Heyreach",
        image: require("@/assets/img/icons/brands/heyreach.png"),
        external_link: "https://app.weezly.com/tutorials/category-11/video-27",
      },
      {
        id: "lagrowthmachine",
        name: "Lagrowthmachine",
        description: "Effortlessly create and send AI-powered videos, and schedule meetings with your leads—all seamlessly within La Growth Machine.",
        image: require("@/assets/img/icons/brands/lagrowthmachine.svg"),
        external_link: "",
      },
      {
        id: "instantly",
        name: "Instantly",
        description: "Easily create and share AI-sales videos while booking meetings with your leads—all directly through Instantly.",
        image: require("@/assets/img/icons/brands/Instantly.svg"),
        external_link: "https://weezly.com/solutions/instantly/",
      },
    ],
  },
  {
    icon: "la la-users-cog",
    name: "Sales and CRM",
    items: [
      {
        id: "hubspot",
        name: "HubSpot",
        description: "text.int_hubspot_msg",
        image: require("@/assets/img/icons/hubspot.png"),
      },
      {
        id: "pipedrive",
        name: "Pipedrive",
        description: "text.int_pipedrive_msg",
        image: require("@/assets/img/icons/pipedrive.png"),
      },
    ],
  },
  {
    icon: "la la-code",
    name: "Embed",
    items: [
      {
        id: "wordpress",
        name: "Wordpress",
        description: "text.int_wordpress_msg",
        image: require("@/assets/img/icons/wordpress.png"),
      },
      {
        id: "wix",
        name: "Wix",
        description: "text.int_wix_msg",
        image: require("@/assets/img/icons/wix.png"),
      },
      {
        id: "squarespace",
        name: "Squarespace",
        description: "text.int_squarespace_msg",
        image: require("@/assets/img/icons/squarespace.png"),
      },
      {
        id: "unbounce",
        name: "Unbounce",
        description: "text.int_unbounce_msg",
        image: require("@/assets/img/icons/unbounce.png"),
      },
      {
        id: "weebly",
        name: "Weebly",
        description: "text.int_weebly_msg",
        image: require("@/assets/img/icons/weebly.png"),
      },
      {
        id: "shopify",
        name: "Shopify",
        description: "text.int_shopify_meet_msg",
        image: require("@/assets/img/icons/shopify.png"),
      },
    ],
  },
  {
    icon: "la la-puzzle-piece",
    name: "Extensions",
    items: [
      {
        id: "chrome",
        name: "Weezly for Chrome",
        description: "Get access to all your schedulings & meetings instantly in Chrome.",
        image: require("@/assets/img/icons/chrome.png"),
        external_link: "https://chromewebstore.google.com/detail/screen-recorder-schedulin/kijbihhjhdiipiljfhboknmdipgphoel",
      },
      // {
      //   id: "firefox",
      //   name: "Weezly for Firefox",
      //   description: "Download the Firefox Extension and manage all your events in there.",
      //   image: require("@/assets/img/icons/firefox.png"),
      //   external_link: "https://addons.mozilla.org/en-US/firefox/addon/callendo-scheduling-with-video/",
      // },
      // {
      //   id: "edge",
      //   name: "Weezly for Edge",
      //   description: "Instantly access all your schedules and meetings in Edge.",
      //   image: require("@/assets/img/icons/edge.png"),
      //   external_link: "https://callendo.com/docs/integrations/add-callendo-to-microsoft-edge/",
      // },
    ],
  },
  {
    icon: "la la-plug",
    name: "API and connectors",
    items: [
      {
        id: "webhooks",
        name: "Webhooks",
        description: "text.int_webhooks_msg",
        image: require("@/assets/img/icons/webhooks.png"),
      },
      {
        id: "zapier",
        name: "Zapier",
        description: "text.int_zapier_msg",
        image: require("@/assets/img/icons/zapier.png"),
      },
      {
        id: "pabbly_connect",
        name: "Pabbly Connect",
        description: "text.int_pabbly_msg",
        image: require("@/assets/img/icons/pabbly.png"),
      },
    ],
  },
  {
    icon: "la la-shoe-prints",
    name: "Tracking",
    items: [
      {
        id: "fpixel",
        name: "Facebook Pixel",
        description: "Use Facebook Pixel to track video views with triggers",
        image: require("@/assets/img/icons/facebook-pixel.png"),
      },
      {
        id: "gtag",
        name: "Google Tag Manager",
        description: "Use Google Tag Manager to track video views with triggers",
        image: require("@/assets/img/icons/google-tag-manager.png"),
      },
      {
        id: "ga4",
        name: "Google Analytics 4",
        description: "Use Google Analytics 4 to track video views with triggers",
        image: require("@/assets/img/icons/google-analytics-4.png"),
      },
    ],
  },
];

const illegal_urls = [
  "admin",
  "administrator",
  "null",
  "undefined",
  "root",
  "user",
  "users",
  "false",
  "true",
  "password",
  "unknown",
  "anonymous",
  "api",
  "login",
  "forgotpassword",
  "onboard",
  "settings",
  "availability",
  "scheduled-events",
  "integrations",
  "calendar-connections",
  "analytics",
  "reschedule",
  "cancel",
  "blog",
  "product",
  "pricing",
  "contact-us",
  "terms",
  "privacy",
  "wp-admin",
  "try",
  "docs",
  "video_embed",
  "bookingpage",
  "timeslotpicker",
  "groupinvite",
  "widget",
  "upgrade",
  "payment-success",
  "screencast",
  "externaltimeslot",
  "dynamic-videos",
  "external-dynamic",
  "create-video-embed",
  "request-calendar",
  "organization",
  "developer",
  "developers",
  "api",
  "tutorial",
  "tutorials",
  "client",
  "clients",
];

const convertYoutube = (link) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = link.match(regExp);

  let videoID = match && match[2].length === 11 ? match[2] : null;

  return "https://www.youtube.com/embed/" + videoID;
};
const convertVimeo = (link) => {
  let arr = ["vimeo.com/", "https://vimeo.com/", "https://www.vimeo.com/"];

  for (let i = 0; i < arr.length; i++) {
    if (link.startsWith(arr[i])) {
      link = link.replace(arr[i], "https://player.vimeo.com/video/");
      break;
    }
  }
  return link;
};

const getYoutubeThumbnail = (link) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = link.match(regExp);

  let videoID = match && match[2].length === 11 ? match[2] : null;

  return `https://img.youtube.com/vi/${videoID}/mqdefault.jpg`;
};

const formatTime = (time, time_format = "24h") => {
  try {
    if (time.endsWith("am") | time.endsWith("pm")) {
      if (time_format == "12h") {
        return time;
      } else {
        return moment(time, "hh:mma").format("HH:mm");
      }
    } else {
      if (time_format == "12h") {
        return moment(time, "HH:mm").format("hh:mma");
      } else {
        return time;
      }
    }
  } catch (e) {
    return "";
  }
};

const languages = [
  {
    key: "en",
    name: "English",
    flag: "gb",
  },
  {
    key: "sv",
    name: "Swedish",
    flag: "se",
  },
  {
    key: "dk",
    name: "Danish",
    flag: "dk",
  },
  {
    key: "pt",
    name: "Portugish",
    flag: "pt",
  },
  {
    key: "de",
    name: "German",
    flag: "de",
  },
  {
    key: "it",
    name: "Italian",
    flag: "it",
  },
  {
    key: "fr",
    name: "French",
    flag: "fr",
  },
  {
    key: "es",
    name: "Spanish",
    flag: "es",
  },

  {
    key: "ar",
    name: "Arabic",
    flag: "ar",
  },
  {
    key: "cz",
    name: "Czech",
    flag: "cz",
  },
  {
    key: "fi",
    name: "Finnish",
    flag: "fi",
  },
  {
    key: "hu",
    name: "Hungarian",
    flag: "hu",
  },
  {
    key: "il",
    name: "Hebrew",
    flag: "il",
  },
  {
    key: "nl",
    name: "Dutch",
    flag: "nl",
  },
  {
    key: "no",
    name: "Norwegian",
    flag: "no",
  },
  {
    key: "pl",
    name: "Polish",
    flag: "pl",
  },
  {
    key: "tr",
    name: "Turkish",
    flag: "tr",
  },
  {
    key: "uk",
    name: "Ukranian",
    flag: "ua",
  },
  {
    key: "cn",
    name: "Chinese",
    flag: "cn",
  },
  {
    key: "ro",
    name: "Romanian",
    flag: "ro",
  },
  {
    key: "vi",
    name: "Vietnamese",
    flag: "vn",
  },
];

const getBrowserLocale = (options = {}) => {
  const defaultOptions = { countryCodeOnly: true };
  const opt = { ...defaultOptions, ...options };
  const navigatorLocale = navigator.languages !== undefined ? navigator.languages[0] : navigator.language;
  if (!navigatorLocale) {
    return undefined;
  }
  const trimmedLocale = opt.countryCodeOnly ? navigatorLocale.trim().split(/-|_/)[0] : navigatorLocale.trim();
  return trimmedLocale;
};

const convertSeconds = (sec) => {
  let text = "";

  if (sec >= 3600) {
    let hrs = Math.floor(sec / 3600);
    let remainingMin = (sec % 3600) / 60;

    if (remainingMin >= 30) hrs += 0.5;
    text = hrs + " hrs";
  } else if (sec >= 60) {
    let min = sec / 60;
    let roundedMin = Math.ceil(min * 2) / 2; // Round up to the nearest 0.5

    if (roundedMin >= 60) text = "1 hrs";
    else text = roundedMin + " min";
  } else {
    text = sec + " sec";
  }

  return text;
};

const getWall = (slug) => {
  try {
    if (slug == "dynamic-videos-minutes") return store.getters.getMe.quantity_minutes;
    else {
      let config = store.getters.getMe.configs.find((c) => c.slug == slug);
      if (config) return config.data;
      else return false;
    }
  } catch (e) {
    return false;
  }
};

const countries = [
  {
    code: "AF",
    name: "Afghanistan",
  },
  {
    code: "AL",
    name: "Albania",
  },
  {
    code: "DZ",
    name: "Algeria",
  },
  {
    code: "AS",
    name: "American Samoa",
  },
  {
    code: "AD",
    name: "Andorra",
  },
  {
    code: "AO",
    name: "Angola",
  },
  {
    code: "AI",
    name: "Anguilla",
  },
  {
    code: "AQ",
    name: "Antarctica",
  },
  {
    code: "AG",
    name: "Antigua and Barbuda",
  },
  {
    code: "AR",
    name: "Argentina",
  },
  {
    code: "AM",
    name: "Armenia",
  },
  {
    code: "AW",
    name: "Aruba",
  },
  {
    code: "AU",
    name: "Australia",
  },
  {
    code: "AT",
    name: "Austria",
  },
  {
    code: "AZ",
    name: "Azerbaijan",
  },
  {
    code: "BS",
    name: "Bahamas",
  },
  {
    code: "BH",
    name: "Bahrain",
  },
  {
    code: "BD",
    name: "Bangladesh",
  },
  {
    code: "BB",
    name: "Barbados",
  },
  {
    code: "BY",
    name: "Belarus",
  },
  {
    code: "BE",
    name: "Belgium",
  },
  {
    code: "BZ",
    name: "Belize",
  },
  {
    code: "BJ",
    name: "Benin",
  },
  {
    code: "BM",
    name: "Bermuda",
  },
  {
    code: "BT",
    name: "Bhutan",
  },
  {
    code: "BO",
    name: "Bolivia",
  },
  {
    code: "BA",
    name: "Bosnia and Herzegovina",
  },
  {
    code: "BW",
    name: "Botswana",
  },
  {
    code: "BV",
    name: "Bouvet Island",
  },
  {
    code: "BR",
    name: "Brazil",
  },
  {
    code: "IO",
    name: "British Indian Ocean Territory",
  },
  {
    code: "BN",
    name: "Brunei Darussalam",
  },
  {
    code: "BG",
    name: "Bulgaria",
  },
  {
    code: "BF",
    name: "Burkina Faso",
  },
  {
    code: "BI",
    name: "Burundi",
  },
  {
    code: "KH",
    name: "Cambodia",
  },
  {
    code: "CM",
    name: "Cameroon",
  },
  {
    code: "CA",
    name: "Canada",
  },
  {
    code: "CV",
    name: "Cape Verde",
  },
  {
    code: "KY",
    name: "Cayman Islands",
  },
  {
    code: "CF",
    name: "Central African Republic",
  },
  {
    code: "TD",
    name: "Chad",
  },
  {
    code: "CL",
    name: "Chile",
  },
  {
    code: "CN",
    name: "China",
  },
  {
    code: "CX",
    name: "Christmas Island",
  },
  {
    code: "CC",
    name: "Cocos (Keeling) Islands",
  },
  {
    code: "CO",
    name: "Colombia",
  },
  {
    code: "KM",
    name: "Comoros",
  },
  {
    code: "CG",
    name: "Congo",
  },
  {
    code: "CD",
    name: "Congo, Democratic Republic of the",
  },
  {
    code: "CK",
    name: "Cook Islands",
  },
  {
    code: "CR",
    name: "Costa Rica",
  },
  {
    code: "CI",
    name: "Cote d'Ivoire",
  },
  {
    code: "HR",
    name: "Croatia",
  },
  {
    code: "CU",
    name: "Cuba",
  },
  {
    code: "CY",
    name: "Cyprus",
  },
  {
    code: "CZ",
    name: "Czech Republic",
  },
  {
    code: "DK",
    name: "Denmark",
  },
  {
    code: "DJ",
    name: "Djibouti",
  },
  {
    code: "DM",
    name: "Dominica",
  },
  {
    code: "DO",
    name: "Dominican Republic",
  },
  {
    code: "EC",
    name: "Ecuador",
  },
  {
    code: "EG",
    name: "Egypt",
  },
  {
    code: "SV",
    name: "El Salvador",
  },
  {
    code: "GQ",
    name: "Equatorial Guinea",
  },
  {
    code: "ER",
    name: "Eritrea",
  },
  {
    code: "EE",
    name: "Estonia",
  },
  {
    code: "ET",
    name: "Ethiopia",
  },
  {
    code: "FK",
    name: "Falkland Islands (Malvinas)",
  },
  {
    code: "FO",
    name: "Faroe Islands",
  },
  {
    code: "FJ",
    name: "Fiji",
  },
  {
    code: "FI",
    name: "Finland",
  },
  {
    code: "FR",
    name: "France",
  },
  {
    code: "GF",
    name: "French Guiana",
  },
  {
    code: "PF",
    name: "French Polynesia",
  },
  {
    code: "TF",
    name: "French Southern Territories",
  },
  {
    code: "GA",
    name: "Gabon",
  },
  {
    code: "GM",
    name: "Gambia",
  },
  {
    code: "GE",
    name: "Georgia",
  },
  {
    code: "DE",
    name: "Germany",
  },
  {
    code: "GH",
    name: "Ghana",
  },
  {
    code: "GI",
    name: "Gibraltar",
  },
  {
    code: "GR",
    name: "Greece",
  },
  {
    code: "GL",
    name: "Greenland",
  },
  {
    code: "GD",
    name: "Grenada",
  },
  {
    code: "GP",
    name: "Guadeloupe",
  },
  {
    code: "GU",
    name: "Guam",
  },
  {
    code: "GT",
    name: "Guatemala",
  },
  {
    code: "GN",
    name: "Guinea",
  },
  {
    code: "GW",
    name: "Guinea-Bissau",
  },
  {
    code: "GY",
    name: "Guyana",
  },
  {
    code: "HT",
    name: "Haiti",
  },
  {
    code: "HM",
    name: "Heard Island and McDonald Islands",
  },
  {
    code: "VA",
    name: "Holy See (Vatican City State)",
  },
  {
    code: "HN",
    name: "Honduras",
  },
  {
    code: "HK",
    name: "Hong Kong",
  },
  {
    code: "HU",
    name: "Hungary",
  },
  {
    code: "IS",
    name: "Iceland",
  },
  {
    code: "IN",
    name: "India",
  },
  {
    code: "ID",
    name: "Indonesia",
  },
  {
    code: "IR",
    name: "Iran, Islamic Republic of",
  },
  {
    code: "IQ",
    name: "Iraq",
  },
  {
    code: "IE",
    name: "Ireland",
  },
  {
    code: "IL",
    name: "Israel",
  },
  {
    code: "IT",
    name: "Italy",
  },
  {
    code: "JM",
    name: "Jamaica",
  },
  {
    code: "JP",
    name: "Japan",
  },
  {
    code: "JO",
    name: "Jordan",
  },
  {
    code: "KZ",
    name: "Kazakhstan",
  },
  {
    code: "KE",
    name: "Kenya",
  },
  {
    code: "KI",
    name: "Kiribati",
  },
  {
    code: "KP",
    name: "Korea, Democratic People's Republic of",
  },
  {
    code: "KR",
    name: "Korea, Republic of",
  },
  {
    code: "KW",
    name: "Kuwait",
  },
  {
    code: "KG",
    name: "Kyrgyzstan",
  },
  {
    code: "LA",
    name: "Lao People's Democratic Republic",
  },
  {
    code: "LV",
    name: "Latvia",
  },
  {
    code: "LB",
    name: "Lebanon",
  },
  {
    code: "LS",
    name: "Lesotho",
  },
  {
    code: "LR",
    name: "Liberia",
  },
  {
    code: "LY",
    name: "Libyan Arab Jamahiriya",
  },
  {
    code: "LI",
    name: "Liechtenstein",
  },
  {
    code: "LT",
    name: "Lithuania",
  },
  {
    code: "LU",
    name: "Luxembourg",
  },
  {
    code: "MO",
    name: "Macao",
  },
  {
    code: "MK",
    name: "North Macedonia",
  },
  {
    code: "MG",
    name: "Madagascar",
  },
  {
    code: "MW",
    name: "Malawi",
  },
  {
    code: "MY",
    name: "Malaysia",
  },
  {
    code: "MV",
    name: "Maldives",
  },
  {
    code: "ML",
    name: "Mali",
  },
  {
    code: "MT",
    name: "Malta",
  },
  {
    code: "MH",
    name: "Marshall Islands",
  },
  {
    code: "MQ",
    name: "Martinique",
  },
  {
    code: "MR",
    name: "Mauritania",
  },
  {
    code: "MU",
    name: "Mauritius",
  },
  {
    code: "YT",
    name: "Mayotte",
  },
  {
    code: "MX",
    name: "Mexico",
  },
  {
    code: "FM",
    name: "Micronesia, Federated States of",
  },
  {
    code: "MD",
    name: "Moldova, Republic of",
  },
  {
    code: "MC",
    name: "Monaco",
  },
  {
    code: "MN",
    name: "Mongolia",
  },
  {
    code: "ME",
    name: "Montenegro",
  },
  {
    code: "MS",
    name: "Montserrat",
  },
  {
    code: "MA",
    name: "Morocco",
  },
  {
    code: "MZ",
    name: "Mozambique",
  },
  {
    code: "MM",
    name: "Myanmar",
  },
  {
    code: "NA",
    name: "Namibia",
  },
  {
    code: "NR",
    name: "Nauru",
  },
  {
    code: "NP",
    name: "Nepal",
  },
  {
    code: "NL",
    name: "Netherlands",
  },
  {
    code: "AN",
    name: "Netherlands Antilles",
  },
  {
    code: "NC",
    name: "New Caledonia",
  },
  {
    code: "NZ",
    name: "New Zealand",
  },
  {
    code: "NI",
    name: "Nicaragua",
  },
  {
    code: "NE",
    name: "Niger",
  },
  {
    code: "NG",
    name: "Nigeria",
  },
  {
    code: "NU",
    name: "Niue",
  },
  {
    code: "NF",
    name: "Norfolk Island",
  },
  {
    code: "MP",
    name: "Northern Mariana Islands",
  },
  {
    code: "NO",
    name: "Norway",
  },
  {
    code: "OM",
    name: "Oman",
  },
  {
    code: "PK",
    name: "Pakistan",
  },
  {
    code: "PW",
    name: "Palau",
  },
  {
    code: "PS",
    name: "Palestinian Territory, Occupied",
  },
  {
    code: "PA",
    name: "Panama",
  },
  {
    code: "PG",
    name: "Papua New Guinea",
  },
  {
    code: "PY",
    name: "Paraguay",
  },
  {
    code: "PE",
    name: "Peru",
  },
  {
    code: "PH",
    name: "Philippines",
  },
  {
    code: "PN",
    name: "Pitcairn",
  },
  {
    code: "PL",
    name: "Poland",
  },
  {
    code: "PT",
    name: "Portugal",
  },
  {
    code: "PR",
    name: "Puerto Rico",
  },
  {
    code: "QA",
    name: "Qatar",
  },
  {
    code: "RE",
    name: "Reunion",
  },
  {
    code: "RO",
    name: "Romania",
  },
  {
    code: "RU",
    name: "Russian Federation",
  },
  {
    code: "RW",
    name: "Rwanda",
  },
  {
    code: "SH",
    name: "Saint Helena",
  },
  {
    code: "KN",
    name: "Saint Kitts and Nevis",
  },
  {
    code: "LC",
    name: "Saint Lucia",
  },
  {
    code: "PM",
    name: "Saint Pierre and Miquelon",
  },
  {
    code: "VC",
    name: "Saint Vincent and the Grenadines",
  },
  {
    code: "WS",
    name: "Samoa",
  },
  {
    code: "SM",
    name: "San Marino",
  },
  {
    code: "ST",
    name: "Sao Tome and Principe",
  },
  {
    code: "SA",
    name: "Saudi Arabia",
  },
  {
    code: "SN",
    name: "Senegal",
  },
  {
    code: "RS",
    name: "Serbia",
  },
  {
    code: "SC",
    name: "Seychelles",
  },
  {
    code: "SL",
    name: "Sierra Leone",
  },
  {
    code: "SG",
    name: "Singapore",
  },
  {
    code: "SK",
    name: "Slovakia",
  },
  {
    code: "SI",
    name: "Slovenia",
  },
  {
    code: "SB",
    name: "Solomon Islands",
  },
  {
    code: "SO",
    name: "Somalia",
  },
  {
    code: "ZA",
    name: "South Africa",
  },
  {
    code: "GS",
    name: "South Georgia and the South Sandwich Islands",
  },
  {
    code: "ES",
    name: "Spain",
  },
  {
    code: "LK",
    name: "Sri Lanka",
  },
  {
    code: "SD",
    name: "Sudan",
  },
  {
    code: "SR",
    name: "Suriname",
  },
  {
    code: "SJ",
    name: "Svalbard and Jan Mayen",
  },
  {
    code: "SZ",
    name: "Swaziland",
  },
  {
    code: "SE",
    name: "Sweden",
  },
  {
    code: "CH",
    name: "Switzerland",
  },
  {
    code: "SY",
    name: "Syrian Arab Republic",
  },
  {
    code: "TW",
    name: "Taiwan",
  },
  {
    code: "TJ",
    name: "Tajikistan",
  },
  {
    code: "TZ",
    name: "Tanzania, United Republic of",
  },
  {
    code: "TH",
    name: "Thailand",
  },
  {
    code: "TL",
    name: "Timor-Leste",
  },
  {
    code: "TG",
    name: "Togo",
  },
  {
    code: "TK",
    name: "Tokelau",
  },
  {
    code: "TO",
    name: "Tonga",
  },
  {
    code: "TT",
    name: "Trinidad and Tobago",
  },
  {
    code: "TN",
    name: "Tunisia",
  },
  {
    code: "TR",
    name: "Turkey",
  },
  {
    code: "TM",
    name: "Turkmenistan",
  },
  {
    code: "TC",
    name: "Turks and Caicos Islands",
  },
  {
    code: "TV",
    name: "Tuvalu",
  },
  {
    code: "UG",
    name: "Uganda",
  },
  {
    code: "UA",
    name: "Ukraine",
  },
  {
    code: "AE",
    name: "United Arab Emirates",
  },
  {
    code: "GB",
    name: "United Kingdom",
  },
  {
    code: "US",
    name: "United States",
  },
  {
    code: "UM",
    name: "United States Minor Outlying Islands",
  },
  {
    code: "UY",
    name: "Uruguay",
  },
  {
    code: "UZ",
    name: "Uzbekistan",
  },
  {
    code: "VU",
    name: "Vanuatu",
  },
  {
    code: "VE",
    name: "Venezuela",
  },
  {
    code: "VN",
    name: "Vietnam",
  },
  {
    code: "VG",
    name: "Virgin Islands, British",
  },
  {
    code: "VI",
    name: "Virgin Islands, U.S.",
  },
  {
    code: "WF",
    name: "Wallis and Futuna",
  },
  {
    code: "EH",
    name: "Western Sahara",
  },
  {
    code: "YE",
    name: "Yemen",
  },
  {
    code: "ZM",
    name: "Zambia",
  },
  {
    code: "ZW",
    name: "Zimbabwe",
  },
];

export {
  week_days,
  zones,
  getSchedulingProfileURL,
  getSchedulingBrandURL,
  getSchedulingBGURL,
  getSchedulingVideoURL,
  getOverlayURL,
  getRecordingVideoURL,
  getDynamicVideoURL,
  option_locations,
  list_questions,
  newSalt,
  getLocationIcon,
  getLocationName,
  integration_types,
  illegal_urls,
  convertYoutube,
  convertVimeo,
  getYoutubeThumbnail,
  formatTime,
  languages,
  getBrowserLocale,
  convertSeconds,
  getWall,
  countries,
};
